import styled, { css, keyframes } from 'styled-components';
import Modal from 'react-bootstrap/Modal';
import ArrowCardIcon from 'assets/img/campanhas-de-marketing/arrow-card.svg';
import CloseIconImage from 'assets/img/campanhas-de-marketing/close-icon.svg';
import {
  CampaignOnboardingArrowIconProps,
  CampaignOnboardingDotProps,
  CampaignOnboardingImageProps,
} from 'models/campaign-mkt';

const textFade = keyframes`
  from {
    opacity: 0;
  }

  to {
    opacity: 1;
  }
`;
const imageTransition = ({ $direction }: CampaignOnboardingImageProps) => {
  if ($direction === 'initial') {
    return '';
  }

  return css`
    animation: ${textFade} 180ms ease-out;
  `;
};

const textTransition = ({ $direction }: CampaignOnboardingImageProps) => {
  if ($direction === 'initial') {
    return '';
  }

  return css`
    animation: ${textFade} 180ms ease-out;
  `;
};
export const ModalStyled = styled(Modal)`
  backdrop-filter: blur(2px);

  .campaign-onboarding-modal {
    width: 858px;
    max-width: calc(100vw - 32px);

    @media (min-width: 576px) {
      max-width: calc(100vw - 32px);
    }
  }

  .modal-content {
    width: 858px;
    max-width: calc(100vw - 32px);
    height: 534px;
    max-height: calc(100vh - 32px);
    border: 0;
    border-radius: 24px;
    background: #f7f9fc;
    box-shadow: 0px 4px 12px rgba(51, 55, 60, 0.22);
    overflow: hidden;
  }

  @media (max-width: 767px) {
    .modal-content {
      height: auto;
      min-height: 0;
      overflow-y: auto;
    }
  }
`;

export const Content = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  gap: 24px;
  height: 100%;
  padding: 16px 16px 16px 24px;

  @media (max-width: 767px) {
    grid-template-columns: 1fr;
    gap: 18px;
    padding: 18px;
  }
`;

export const TextColumn = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
  padding-top: 74px;

  @media (max-width: 767px) {
    padding: 34px 0 0;
  }
`;

export const TextContent = styled.div<CampaignOnboardingImageProps>`
  ${textTransition};
`;
export const Title = styled.h2`
  margin: 0;
  color: #33373c;
  font-family: 'Roboto Flex', sans-serif;
  font-size: 28px;
  font-weight: 700;
  line-height: 36px;
  letter-spacing: 0;

  @media (max-width: 575px) {
    font-size: 24px;
    line-height: 31px;
  }
`;

export const Text = styled.p`
  margin: 24px 0 0;
  color: #525252;
  font-family: 'Roboto Flex', sans-serif;
  font-size: 16px;
  font-weight: 400;
  line-height: 25px;
  letter-spacing: 0;

  @media (max-width: 575px) {
    margin-top: 20px;
    font-size: 15px;
    line-height: 22px;
  }
`;

export const Navigation = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 24px;
`;

export const ArrowButton = styled.button`
  width: 32px;
  height: 32px;
  box-sizing: border-box;
  padding: 0;
  border: 2px solid #0c50d3;
  border-radius: 50%;
  background: transparent;
  color: #0c50d3;
  line-height: 1;
  display: inline-grid;
  place-items: center;

  &:not(:disabled):hover,
  &:not(:disabled):focus-visible {
    background: #e3f2ff;
    color: #0c50d3;
  }

  &:not(:disabled):active {
    background: #c8e6ff;
    color: #0c50d3;
  }

  &:disabled {
    border-color: #464a50;
    color: #464a50;
    cursor: default;
    opacity: 1;
  }
`;

export const ArrowIcon = styled.span<CampaignOnboardingArrowIconProps>`
  display: block;
  width: 11px;
  height: 11px;
  background: currentColor;
  -webkit-mask: url(${ArrowCardIcon}) center / contain no-repeat;
  mask: url(${ArrowCardIcon}) center / contain no-repeat;
  transform-origin: center;
  transform: rotate(
    ${({ $direction }) => ($direction === 'left' ? '180deg' : '0deg')}
  );
`;

export const ActionButton = styled.button`
  width: 100%;
  height: 40px;
  margin-top: auto;
  margin-bottom: 20px;
  border: 0;
  border-radius: 24px;
  background: #0c50d3;
  color: #fbfbfb;
  font-family: 'Roboto Flex', sans-serif;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  text-align: center;

  &:hover,
  &:focus-visible {
    background: #1670fa;
    box-shadow: 0px 8px 24px rgba(21, 87, 217, 0.2);
  }

  &:active {
    background: #164194;
    box-shadow: none;
  }

  &:disabled {
    background: #e4e9f0;
    color: #98a2b3;
    cursor: default;
    box-shadow: none;
  }

  @media (max-width: 767px) {
    margin-top: 32px;
  }
`;

export const ImageColumn = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  min-width: 0;
  height: 100%;
`;

export const CloseButton = styled.button`
  position: absolute;
  top: 3px;
  right: 3px;
  z-index: 1;
  width: 32px;
  height: 32px;
  padding: 0;
  border: 0;
  border-radius: 50%;
  background: transparent;
  color: #33373c;
  line-height: 1;
  display: inline-grid;
  place-items: center;

  &:hover,
  &:focus-visible {
    background: #e7eaef;
  }

  &:active {
    background: #c8e6ff;
    color: #0c50d3;
  }
`;

export const CloseIcon = styled.span`
  width: 16px;
  height: 16px;
  background: currentColor;
  -webkit-mask: url(${CloseIconImage}) center / contain no-repeat;
  mask: url(${CloseIconImage}) center / contain no-repeat;
`;

export const Image = styled.img<CampaignOnboardingImageProps>`
  width: 100%;
  min-height: 0;
  flex: 1;
  object-fit: cover;
  object-position: center;
  border-radius: 8px;
  will-change: opacity;
  ${imageTransition};

  @media (max-width: 767px) {
    height: 280px;
    flex: none;
  }
`;

export const Dots = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  height: 20px;
  padding-top: 10px;
`;

export const Dot = styled.button<CampaignOnboardingDotProps>`
  width: 8px;
  height: 8px;
  padding: 0;
  border: 0;
  border-radius: 50%;
  background: ${({ $active }) => ($active ? '#0c50d3' : '#d7dee7')};
  cursor: ${({ $active }) => ($active ? 'default' : 'pointer')};

  &:not(:disabled):hover,
  &:not(:disabled):focus-visible {
    background: #0c50d3;
  }
`;
